/**
 * Accessibility utilities for CHM Construction website
 * Implements WCAG 2.1 AA compliance and accessibility best practices
 */

// Focus management utilities
export const trapFocus = (element: HTMLElement) => {
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstElement = focusableElements[0] as HTMLElement;
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

  const handleTabKey = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    }
  };

  element.addEventListener('keydown', handleTabKey);
  return () => element.removeEventListener('keydown', handleTabKey);
};

// ARIA utilities
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.setAttribute('class', 'sr-only');
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

// Skip link utility
export const createSkipLink = () => {
  const skipLink = document.createElement('a');
  skipLink.href = '#main-content';
  skipLink.textContent = 'Skip to main content';
  skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded';
  
  document.body.insertBefore(skipLink, document.body.firstChild);
};

// Color contrast checker
export const checkColorContrast = (foreground: string, background: string): boolean => {
  // Simple contrast ratio checker (would need full implementation for production)
  // This is a placeholder for contrast checking logic
  const luminance = (color: string) => {
    // Convert hex to RGB and calculate luminance
    // Implementation would go here
    return 0.5; // Placeholder
  };
  
  const ratio = (luminance(foreground) + 0.05) / (luminance(background) + 0.05);
  return ratio >= 4.5; // WCAG AA standard
};

// Keyboard navigation utilities
export const addKeyboardNavigation = (element: HTMLElement, onActivate: () => void) => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  };
  
  element.addEventListener('keydown', handleKeyDown);
  element.setAttribute('tabindex', '0');
  element.setAttribute('role', 'button');
  
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
    element.removeAttribute('tabindex');
    element.removeAttribute('role');
  };
};

// Screen reader friendly loading states
export const createLoadingIndicator = (element: HTMLElement, loadingText: string = 'Loading...') => {
  element.setAttribute('aria-busy', 'true');
  element.setAttribute('aria-label', loadingText);
  
  return () => {
    element.removeAttribute('aria-busy');
    element.removeAttribute('aria-label');
  };
};

// Form accessibility utilities
export const enhanceFormAccessibility = (form: HTMLFormElement) => {
  const inputs = form.querySelectorAll('input, select, textarea');
  
  inputs.forEach((input) => {
    const label = form.querySelector(`label[for="${input.id}"]`);
    
    if (!label && input.id) {
      console.warn(`Input with id "${input.id}" has no associated label`);
    }
    
    // Add ARIA attributes for required fields
    if (input.hasAttribute('required')) {
      input.setAttribute('aria-required', 'true');
    }
    
    // Add error message association
    const errorElement = form.querySelector(`[data-error-for="${input.id}"]`);
    if (errorElement) {
      input.setAttribute('aria-describedby', errorElement.id);
    }
  });
};

// Reduce motion utility
export const respectsReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export default {
  trapFocus,
  announceToScreenReader,
  createSkipLink,
  checkColorContrast,
  addKeyboardNavigation,
  createLoadingIndicator,
  enhanceFormAccessibility,
  respectsReducedMotion
};